import React from "react";
import { Quote, Wand2, Link as LinkIcon, BookOpen, Video } from "lucide-react";

export type FieldKey = "title" | "content" | "author" | "url" | "complexity";

export interface SelectOption {
  value: string;
  label: string;
}

export interface FieldConfig {
  key: FieldKey;
  label: string;
  placeholder?: string;
  type?: "text" | "textarea" | "url" | "select";
  required?: boolean;
  rows?: number;
  helper?: string;
  // Only used when type === "select".
  options?: SelectOption[];
}

// Shared across the AI Prompt create/edit forms, the card badge, and the
// /ai-prompts complexity filter, so every part of the feature agrees on the
// same three levels, labels and colors.
export type AiPromptComplexity = "beginner" | "intermediate" | "advanced";

export const AI_PROMPT_COMPLEXITY_LEVELS: AiPromptComplexity[] = ["beginner", "intermediate", "advanced"];

export const AI_PROMPT_COMPLEXITY_META: Record<AiPromptComplexity, { label: string; badgeClass: string }> = {
  beginner: {
    label: "Beginner",
    badgeClass:
      "bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-700 dark:hover:bg-emerald-500/20",
  },
  intermediate: {
    label: "Intermediate",
    badgeClass:
      "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-700 dark:hover:bg-amber-500/20",
  },
  advanced: {
    label: "Advanced",
    badgeClass:
      "bg-rose-50 text-rose-700 border-rose-300 hover:bg-rose-100 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-700 dark:hover:bg-rose-500/20",
  },
};

export const AI_PROMPT_COMPLEXITY_OPTIONS: SelectOption[] = AI_PROMPT_COMPLEXITY_LEVELS.map((level) => ({
  value: level,
  label: AI_PROMPT_COMPLEXITY_META[level].label,
}));

// Normalizes anything that comes back from the API (or user input) to a
// known level, defaulting to "beginner" - mirrors the backend's normalizer.
export const normalizeAiPromptComplexity = (raw: unknown): AiPromptComplexity => {
  const value = String(raw ?? "").trim().toLowerCase();
  return (AI_PROMPT_COMPLEXITY_LEVELS as string[]).includes(value) ? (value as AiPromptComplexity) : "beginner";
};

export const TYPE_META = {
  quote: {
    icon: <Quote className="w-4 h-4" />,
    label: "Quotes",
    color: "text-slate-700 dark:text-slate-300",
    bg: "bg-slate-100 border-slate-300 dark:bg-slate-700 dark:border-slate-500",
  },
  prompt: {
    icon: <Wand2 className="w-4 h-4" />,
    label: "AI Prompts",
    color: "text-slate-700 dark:text-slate-300",
    bg: "bg-slate-100 border-slate-300 dark:bg-slate-700 dark:border-slate-500",
  },
  article: {
    icon: <LinkIcon className="w-4 h-4" />,
    label: "Articles",
    color: "text-slate-700 dark:text-slate-300",
    bg: "bg-slate-100 border-slate-300 dark:bg-slate-700 dark:border-slate-500",
  },
  book: {
    icon: <BookOpen className="w-4 h-4" />,
    label: "Books",
    color: "text-slate-700 dark:text-slate-300",
    bg: "bg-slate-100 border-slate-300 dark:bg-slate-700 dark:border-slate-500",
  },
  video: {
    icon: <Video className="w-4 h-4" />,
    label: "Videos",
    color: "text-slate-700 dark:text-slate-300",
    bg: "bg-slate-100 border-slate-300 dark:bg-slate-700 dark:border-slate-500",
  },
} as const;

export const FIELD_LAYOUT: Record<string, FieldConfig[]> = {
  article: [
    { key: "title", label: "Article Title *", placeholder: "Enter article headline", required: true },
    { key: "url", label: "Article URL *", placeholder: "https://example.com/inspiration", type: "url", required: true },
  ],
  video: [
    { key: "title", label: "Video Title *", placeholder: "Enter video title", required: true },
    { key: "url", label: "Video URL *", placeholder: "https://youtube.com/...", type: "url", required: true },
  ],
  quote: [
    { key: "content", label: "Quote *", placeholder: "“The best way to get started...”", type: "textarea", rows: 4, required: true },
    { key: "author", label: "Author *", placeholder: "Who said this?", required: true },
  ],
  prompt: [
    // No "title" field: aiprompts have no title column in the database -
    // updateContent only ever saves `content` for this type, matching the
    // single-field layout the create flow already uses.
    { key: "content", label: "Prompt Instructions *", placeholder: "Paste your full AI prompt", type: "textarea", rows: 5, required: true },
    { key: "complexity", label: "Complexity Level *", type: "select", required: true, options: AI_PROMPT_COMPLEXITY_OPTIONS },
  ],
  book: [
    { key: "title", label: "Book Title *", placeholder: "Enter book title", required: true },
    { key: "content", label: "Summary *", placeholder: "Key ideas, takeaways, or summary", type: "textarea", rows: 4, required: true },
    { key: "author", label: "Author *", placeholder: "Who wrote this book?", required: true },
    { key: "url", label: "Reference URL", placeholder: "https://publisher.com/...", type: "url" },
  ],
  default: [
    { key: "title", label: "Title *", placeholder: "Enter title", required: true },
    { key: "content", label: "Content", placeholder: "Add supporting content", type: "textarea", rows: 4 },
  ],
};
